<template>
    <SmithsChartCard :has-data="hasData" :loading="loading" title="Daily Availability">
        <template #sidebar>
            <SidebarKpi label="Overall Availability" :value="formatPercent(data?.overallPct)" />
            <SidebarKpi :label="`Days under ${data?.target ?? 99.5}%`" :value="data?.daysUnderTarget ?? 0" />
        </template>
        <c-chart :key="componentKey" :custom-tooltips="false" :data="chartDataset" :options="chartOptions" class="h-full" type="line" />
    </SmithsChartCard>
</template>

<script setup>
import { computed, ref } from 'vue';
import { CChart } from '@coreui/vue-chartjs';
import SmithsChartCard from './SmithsChartCard.vue';
import SidebarKpi from './SidebarKpi.vue';
import { getBaseChartOptions, chartColors, lightTooltip } from '@/Utils/chartDefaults';

const props = defineProps({
    data: { type: Object, default: null },
    loading: { type: Boolean, default: false },
});

const componentKey = ref(0);
const hasData = computed(() => props.data?.labels?.length > 0);

function formatPercent(value) {
    if (value == null) return '—';
    return new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value) + '%';
}

const chartDataset = computed(() => {
    if (!hasData.value) return { labels: [], datasets: [] };

    return {
        labels: props.data.labels,
        datasets: [
            {
                label: 'Availability %',
                data: props.data.values,
                backgroundColor: chartColors.primaryFill,
                borderColor: chartColors.primary,
                borderWidth: 1.5,
                fill: true,
                tension: 0.3,
                pointRadius: (ctx) => {
                    const status = props.data.reviewStatus?.[ctx.dataIndex];
                    return status === 'pending' ? 5 : 0;
                },
                pointBackgroundColor: (ctx) => {
                    const status = props.data.reviewStatus?.[ctx.dataIndex];
                    return status === 'pending' ? chartColors.amber : chartColors.primary;
                },
            },
        ],
    };
});

const chartOptions = computed(() => ({
    ...getBaseChartOptions(),
    interaction: { mode: 'index', intersect: false },
    scales: {
        x: {
            grid: { display: false },
            ticks: { font: { size: 10 }, maxRotation: 45 },
        },
        y: {
            min: Math.max(90, Math.floor((Math.min(...(props.data?.values?.filter((v) => v != null) ?? [100])) - 2) / 2) * 2),
            max: 101,
            afterBuildTicks: (axis) => {
                axis.ticks = axis.ticks.filter((t) => t.value <= 100);
            },
            grid: { drawBorder: false },
            ticks: { callback: (value) => value + '%' },
        },
    },
    plugins: {
        ...getBaseChartOptions().plugins,
        annotation: {
            annotations: {
                target: {
                    type: 'line',
                    yMin: props.data?.target ?? 99.5,
                    yMax: props.data?.target ?? 99.5,
                    borderColor: chartColors.thresholdLine,
                    borderWidth: 1,
                    borderDash: [5, 5],
                },
            },
        },
        tooltip: {
            ...lightTooltip,
            enabled: true,
            mode: 'index',
            intersect: false,
            callbacks: {
                label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y}%`,
                afterLabel: (ctx) => {
                    const status = props.data?.reviewStatus?.[ctx.dataIndex];
                    return status === 'pending' ? '⚠ Pending review' : '';
                },
            },
        },
    },
}));
</script>
